import React, { PropsWithChildren, ReactNode } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { SectionTitle } from './StyledComponent';

/** Standard page wrapper: consistent padding and max width for settings-style pages. */
export function PageContainer(props: PropsWithChildren<{ maxWidth?: number | string; dense?: boolean }>) {
  return <Box sx={{ p: props.dense ? '10px 4px 24px' : '10px 16px 24px', maxWidth: props.maxWidth ?? 960 }}>{props.children}</Box>;
}

/** Page title with an optional one-line description and actions aligned to the right. */
export function PageHeader(props: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  const { title, description, actions } = props;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
      <Box>
        <SectionTitle as="h1" style={{ margin: 0 }}>
          {title}
        </SectionTitle>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        )}
      </Box>
      {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>{actions}</Box>}
    </Box>
  );
}

/** A titled card grouping related settings. */
export function Section(props: PropsWithChildren<{ title: ReactNode; description?: ReactNode; actions?: ReactNode }>) {
  const { title, description, actions, children } = props;
  return (
    <Paper variant="outlined" component="section" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: children ? 2 : 0 }}>
        <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: '1.05rem', fontWeight: 600, color: '#1f2937' }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          )}
        </Box>
        {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>{actions}</Box>}
      </Box>
      {children}
    </Paper>
  );
}

/** Label / value row for read-only details. */
export function DetailRow(props: { label: string; value: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, gap: { xs: 0, sm: 2 }, py: 1, borderTop: '1px solid #eee' }}>
      <Typography variant="body2" color="text.secondary">
        {props.label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
        {props.value}
      </Typography>
    </Box>
  );
}
